/**
 * RepConnect - Provider Profile Management View
 * 
 * Allows healthcare providers to manage their own profile information,
 * verify enhancements contributed by others, and control their professional data.
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building, MapPin, Star, Calendar, 
  Coffee, Clock, Heart, Edit3, Save, X, 
  Lock, Globe, Users, Eye, Plus, Check, Info, 
  AlertCircle, CheckCircle, Shield, Badge
} from 'lucide-react';
import { ENHANCEMENT_FIELDS, ENHANCEMENT_CATEGORIES, PRIVACY_LEVELS, DATA_SOURCES } from '../utils/profileEnhancements.js';
import ProfileEnhancementStorage from '../utils/profileEnhancementStorage.js';
import { UserStorage } from '../utils/userStorage.js';
import { logger } from '../utils/logger';

const ProviderProfileManagementView = ({ currentUser, linkedNPIProfile, setCurrentView }) => {
  const [enhancements, setEnhancements] = useState({});
  const [unverifiedEnhancements, setUnverifiedEnhancements] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editPrivacy, setEditPrivacy] = useState(PRIVACY_LEVELS.PUBLIC);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (currentUser && linkedNPIProfile) {
      loadProfileData();
    }
  }, [currentUser, linkedNPIProfile]);

  const loadProfileData = () => {
    if (!currentUser || !linkedNPIProfile) return;

    // Load enhancements for their profile
    const profileEnhancements = ProfileEnhancementStorage.getProviderEnhancements(
      linkedNPIProfile.id, 
      currentUser.id, 
      currentUser.userType,
      null,
      currentUser.id // Profile owner ID
    );
    setEnhancements(profileEnhancements);

    // Load unverified enhancements that need their review
    const unverified = ProfileEnhancementStorage.getUnverifiedEnhancements(
      linkedNPIProfile.id,
      currentUser.id
    );
    setUnverifiedEnhancements(unverified);
  };

  const handleEditField = (fieldName, currentValue = '', currentPrivacy = PRIVACY_LEVELS.PUBLIC) => {
    setEditField(fieldName);
    setEditValue(Array.isArray(currentValue) ? currentValue.join(', ') : currentValue);
    setEditPrivacy(currentPrivacy);
    setEditMode(true);
  };

  const handleSaveField = async () => {
    if (!currentUser || !editField || !linkedNPIProfile) return;

    try {
      let processedValue = editValue.trim();
      
      // Handle array fields
      const field = ENHANCEMENT_FIELDS[editField];
      if (field && field.type === 'array') {
        processedValue = processedValue.split(',').map(item => item.trim()).filter(item => item);
      }

      await ProfileEnhancementStorage.addEnhancementWithPermissions(
        linkedNPIProfile.id,
        editField,
        processedValue,
        currentUser.id,
        editPrivacy,
        currentUser.id, // Profile owner ID
        currentUser.userType
      );

      // Reload data
      loadProfileData();
      
      // Reset edit state
      setEditMode(false);
      setEditField(null);
      setEditValue('');
    } catch (error) {
      logger.error('Error saving enhancement:', error);
      alert('Failed to save enhancement. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditField(null);
    setEditValue('');
    setNewFieldName('');
    setShowAddField(false);
  };

  const handleAddNewField = () => {
    if (newFieldName && ENHANCEMENT_FIELDS[newFieldName]) {
      const field = ENHANCEMENT_FIELDS[newFieldName];
      setEditField(newFieldName);
      setEditValue('');
      setEditPrivacy(field.defaultPrivacy);
      setShowAddField(false);
      setNewFieldName('');
    }
  };

  const handleVerifyEnhancement = async (enhancementId) => {
    try {
      await ProfileEnhancementStorage.verifyEnhancement(
        enhancementId,
        currentUser.id,
        currentUser.id
      );
      
      loadProfileData();
      alert('Enhancement verified successfully!');
    } catch (error) {
      logger.error('Error verifying enhancement:', error);
      alert('Failed to verify enhancement.');
    }
  };

  const getPrivacyIcon = (privacyLevel) => {
    switch (privacyLevel) {
      case PRIVACY_LEVELS.PUBLIC:
        return <Globe className="w-3 h-3 text-green-600" title="Public - Visible to all users" />;
      case PRIVACY_LEVELS.PROFESSIONAL:
        return <Users className="w-3 h-3 text-blue-600" title="Professional - Visible to healthcare professionals" />;
      case PRIVACY_LEVELS.PRIVATE:
        return <Lock className="w-3 h-3 text-red-600" title="Private - Only visible to you" />;
      case PRIVACY_LEVELS.TEAM:
        return <Users className="w-3 h-3 text-purple-600" title="Team - Visible to your organization" />;
      default:
        return <Eye className="w-3 h-3 text-gray-600" />;
    }
  };

  const getDataSourceBadge = (dataSource, verified) => {
    const source = Object.values(DATA_SOURCES).find(s => s.type === dataSource);
    if (!source) return null;
    
    return (
      <div className="flex items-center space-x-1">
        <span 
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs ml-2 ${
            verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}
          title={source.description}
        >
          <span className="mr-1">{source.icon}</span>
          {source.label}
        </span>
        {verified && (
          <CheckCircle className="w-3 h-3 text-green-600" title="Verified by provider" />
        )}
      </div>
    );
  };

  const renderEnhancedField = (fieldName, defaultValue, icon) => {
    const enhancement = enhancements[fieldName];
    const value = enhancement ? enhancement.current.value : defaultValue;
    const isEditing = editMode && editField === fieldName;
    const permissions = ProfileEnhancementStorage.getEditPermissions(
      linkedNPIProfile?.id, 
      currentUser?.id, 
      currentUser?.userType, 
      currentUser?.id
    );

    if (!value && !permissions.canAddEnhancements) return null;

    return (
      <div className="flex items-start space-x-3 group">
        {icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-medium text-gray-700">
              {ENHANCEMENT_FIELDS[fieldName]?.label || fieldName}
              {enhancement && getDataSourceBadge(enhancement.current.dataSource, enhancement.current.verified)}
            </p>
            {!isEditing && permissions.canAddEnhancements && (
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {enhancement && getPrivacyIcon(enhancement.current.privacyLevel)}
                <button
                  onClick={() => handleEditField(fieldName, value, enhancement?.current.privacyLevel)}
                  className="text-blue-600 hover:text-blue-700"
                  title="Edit this field"
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          
          {isEditing ? (
            <div className="mt-2 space-y-3">
              <div>
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={ENHANCEMENT_FIELDS[fieldName]?.placeholder}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-gray-600">Privacy:</label>
                  <select
                    value={editPrivacy}
                    onChange={(e) => setEditPrivacy(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    {permissions.allowedPrivacyLevels.map(level => (
                      <option key={level} value={level}>
                        {level === PRIVACY_LEVELS.PUBLIC ? 'Public' :
                         level === PRIVACY_LEVELS.PROFESSIONAL ? 'Professional' :
                         level === PRIVACY_LEVELS.PRIVATE ? 'Private' : 'Team'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveField}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    <Save className="w-3 h-3" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center space-x-1 px-3 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
                  >
                    <X className="w-3 h-3" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-600">
                {Array.isArray(value) ? value.join(', ') : value || 'Not specified'}
              </p>
              {enhancement && enhancement.current.contributorId !== currentUser.id && (
                <p className="text-xs text-gray-500 mt-1">
                  Added by {enhancement.current.contributorType === 'sales' ? 'sales representative' : 'healthcare professional'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getFilteredFields = () => {
    if (selectedCategory === 'all') {
      return Object.keys(ENHANCEMENT_FIELDS);
    }
    return Object.keys(ENHANCEMENT_FIELDS).filter(key => 
      ENHANCEMENT_FIELDS[key].category === selectedCategory
    );
  };

  if (!currentUser) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to manage your profile</p>
        </div>
      </div>
    );
  }

  if (!linkedNPIProfile) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">NPI Profile Not Linked</h3>
          <p className="text-gray-600 mb-4">
            To manage your profile, you need to link your account with your NPI profile first.
          </p>
          <button
            onClick={() => setCurrentView('link-npi')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Link NPI Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Manage Your Profile</h2>
                <p className="text-gray-600">{linkedNPIProfile.name} • {linkedNPIProfile.specialty}</p>
                <div className="flex items-center mt-1">
                  <Badge className="w-4 h-4 text-green-600 mr-1" />
                  <span className="text-sm text-green-600 font-medium">Verified Provider</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setCurrentView('dashboard')}
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Unverified Enhancements Alert */}
        {unverifiedEnhancements.length > 0 && (
          <div className="p-6 border-b bg-yellow-50">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">
                  {unverifiedEnhancements.length} Enhancement{unverifiedEnhancements.length !== 1 ? 's' : ''} Need Your Review
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Other users have added information to your profile. Review and verify what's accurate.
                </p>
                <div className="mt-3 space-y-2">
                  {unverifiedEnhancements.slice(0, 3).map(enhancement => (
                    <div key={enhancement.id} className="flex items-center justify-between bg-white rounded p-2">
                      <div>
                        <span className="text-sm font-medium">{ENHANCEMENT_FIELDS[enhancement.fieldName]?.label}</span>
                        <span className="text-sm text-gray-600 ml-2">: {enhancement.value}</span>
                      </div>
                      <button
                        onClick={() => handleVerifyEnhancement(enhancement.id)}
                        className="flex items-center space-x-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        <Check className="w-3 h-3" />
                        <span>Verify</span>
                      </button>
                    </div>
                  ))}
                  {unverifiedEnhancements.length > 3 && (
                    <p className="text-xs text-yellow-600">
                      And {unverifiedEnhancements.length - 3} more...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="p-6 border-b">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">View:</span>
            <div className="flex space-x-2">
              {[
                { key: 'all', label: 'All Fields', icon: Eye },
                { key: 'contact', label: 'Contact', icon: Phone },
                { key: 'office', label: 'Office', icon: Building },
                { key: 'preferences', label: 'Preferences', icon: Coffee },
                { key: 'professional', label: 'Professional', icon: User }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm ${
                    selectedCategory === key
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Profile Enhancement Fields */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedCategory === 'all' ? 'Profile Information' : ENHANCEMENT_CATEGORIES[selectedCategory]?.label}
                </h3>
                {!editMode && (
                  <button
                    onClick={() => setShowAddField(!showAddField)}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Info</span>
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {/* Core NPI data */}
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-700">Practice Address</p>
                    <p className="text-gray-600">{linkedNPIProfile.address}</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                      <span className="mr-1">🏛️</span>
                      NPI Registry
                    </span>
                  </div>
                </div>

                {/* Enhanced fields */}
                {getFilteredFields().slice(0, Math.ceil(getFilteredFields().length / 2)).map(fieldName => {
                  const field = ENHANCEMENT_FIELDS[fieldName];
                  const icon = fieldName.includes('phone') ? <Phone className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('email') ? <Mail className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('office') || fieldName.includes('parking') ? <Building className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('time') ? <Clock className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('restaurant') || fieldName.includes('dietary') ? <Coffee className="w-5 h-5 text-gray-400 mt-1" /> :
                              <User className="w-5 h-5 text-gray-400 mt-1" />;
                  
                  return renderEnhancedField(fieldName, '', icon);
                })}
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="space-y-4">
                {getFilteredFields().slice(Math.ceil(getFilteredFields().length / 2)).map(fieldName => {
                  const field = ENHANCEMENT_FIELDS[fieldName];
                  const icon = fieldName.includes('phone') ? <Phone className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('email') ? <Mail className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('office') || fieldName.includes('parking') ? <Building className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('time') ? <Clock className="w-5 h-5 text-gray-400 mt-1" /> :
                              fieldName.includes('restaurant') || fieldName.includes('dietary') ? <Coffee className="w-5 h-5 text-gray-400 mt-1" /> :
                              <User className="w-5 h-5 text-gray-400 mt-1" />;
                  
                  return renderEnhancedField(fieldName, '', icon);
                })}
              </div>
            </div>
          </div>

          {/* Add field interface */}
          {showAddField && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <select
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select field to add...</option>
                  {Object.entries(ENHANCEMENT_FIELDS)
                    .filter(([key, field]) => {
                      const permissions = ProfileEnhancementStorage.getEditPermissions(
                        linkedNPIProfile?.id, 
                        currentUser?.id, 
                        currentUser?.userType, 
                        currentUser?.id
                      );
                      return permissions.allowedCategories.includes(field.category) && !enhancements[key];
                    })
                    .map(([key, field]) => (
                      <option key={key} value={key}>{field.label}</option>
                    ))}
                </select>
                <button
                  onClick={handleAddNewField}
                  disabled={!newFieldName}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowAddField(false)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderProfileManagementView;